/* istanbul ignore file */

import { isFiniteNumberString } from './numberStrings';
import { tokenizePath } from './paths';

/**
 * Reads a nested value from a JSON-ish object using a dotted path.
 *
 * Supported forms:
 * - Dot segments: `foo.bar.baz`
 * - Bracket indices: `items[0].name`
 *
 * Notes:
 * - This is intentionally small and purpose-built for engine value trees.
 * - It does not attempt to support arbitrary JS expressions inside brackets.
 */
export function getValueAtPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    // Normalize bracket indices to dot notation so lookups can walk simple segments.
    // Example: `a[0].b` -> `a.0.b`
    const normalized = path.replace(/\[(\d+)\]/g, '.$1');
    const tokens = normalized.split('.').filter(Boolean);
    let current = obj;
    for (const t of tokens) {
        if (current == null) return undefined;
        // Convert number-like segments to array indices when possible.
        const key = isFiniteNumberString(t) ? Number(t) : t;
        current = current[key as any];
    }
    return current;
}

/**
 * Writes a nested value into a JSON-ish object using a dotted/bracketed path.
 *
 * Supported forms:
 * - Dot segments: `foo.bar.baz`
 * - Bracket indices: `items[0].name`
 *
 * Return value:
 * - `true` when the value was written.
 * - `false` when the write was rejected (e.g. invalid path or createMissing=false and an intermediate segment is absent).
 *
 * `createMissing`:
 * - When true (default), missing intermediate objects/arrays are created as needed.
 * - When false, the function is strict: it will not create/overwrite intermediate containers.
 */
export function setValueAtPath(
    obj: any,
    path: string,
    value: unknown,
    options?: {
        createMissing?: boolean;
    }
): boolean {
    if (!obj || !path) return false;

    const createMissing = options?.createMissing ?? true;

    // Tokenize to support both `a.b` and `a[0].b` while preserving the intent of indices.
    const tokens = tokenizePath(path);
    if (!tokens.length) return false;

    const segments: Array<string | number> = tokens.map((t) => (t.type === 'index' ? t.index : t.key));

    let current = obj;
    for (let i = 0; i < segments.length; i++) {
        const key = segments[i];
        const isLeaf = i === segments.length - 1;
        const next = segments[i + 1];
        const nextIsIndex = typeof next === 'number';

        if (isLeaf) {
            current[key as any] = value as any;
            return true;
        }

        const existing = current[key as any];
        const shouldBeArray = nextIsIndex;

        // Ensure the intermediate container exists and matches the required container type.
        // - If the next segment is an index, we need an array.
        // - Otherwise, we need a plain object.
        if (existing === undefined || existing === null) {
            if (!createMissing) return false;
            current[key as any] = shouldBeArray ? [] : {};
        } else if (typeof existing !== 'object') {
            if (!createMissing) return false;
            current[key as any] = shouldBeArray ? [] : {};
        } else if (shouldBeArray && !Array.isArray(existing)) {
            if (!createMissing) return false;
            current[key as any] = [];
        }

        current = current[key as any];
    }

    return false;
}
